import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ADMIN_SUBMENU_LABELS, ADMIN_SUBMENU_ROUTES } from '../constants/constants';

export interface NavigationItem {
  label: string;
  paths: string | string[];
}

/**
 * Custom hook to manage active state of navigation buttons based on current route
 * @param navigationItems - Array of navigation items with their associated paths
 */
export const useActiveNavigation = (navigationItems: NavigationItem[]) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const ensureAdminSubmenu = (buttonElement: HTMLElement) => {
      const parentElement = buttonElement.parentElement;

      if (!parentElement) {
        return;
      }

      parentElement.setAttribute('data-admin-menu-container', 'true');
      buttonElement.setAttribute('data-admin-view-button', 'true');

      const existingSubmenu = parentElement.querySelector<HTMLElement>(
        '[data-admin-submenu="true"]'
      );

      if (existingSubmenu) {
        return;
      }

      const submenu = document.createElement('div');
      submenu.setAttribute('data-admin-submenu', 'true');
      submenu.setAttribute('aria-hidden', 'true');

      ADMIN_SUBMENU_LABELS.forEach((label) => {
        const submenuItem = document.createElement('a');
        submenuItem.setAttribute('data-admin-submenu-item', 'true');
        const route = ADMIN_SUBMENU_ROUTES[label];
        if (route) {
          submenuItem.setAttribute('href', route);
          submenuItem.style.cursor = 'pointer';
          submenuItem.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            navigate(route);
          });
        }
        submenuItem.textContent = label;
        submenu.appendChild(submenuItem);
      });

      parentElement.appendChild(submenu);
    };

    const updateActiveNavigation = () => {
      const buttons = document.querySelectorAll('header .MuiButtonBase-root.MuiButton-root');
      const currentPath = location.pathname;

      if (buttons.length === 0) {
        return;
      }

      buttons.forEach((button) => {
        const buttonElement = button as HTMLElement;
        // Prefer a stable data attribute over textContent; fall back to text for backward compatibility
        const navId =
          buttonElement.getAttribute('data-nav-id') ?? buttonElement.textContent?.trim() ?? '';

        if (navId === 'Admin View') {
          ensureAdminSubmenu(buttonElement);
        }

        // Remove active class from all buttons first
        buttonElement.classList.remove('active');

        if (!navId) {
          return;
        }

        // Find matching navigation item using a stable identifier
        const navItem = navigationItems.find((item) => item.label === navId);

        if (navItem) {
          const pathArray = Array.isArray(navItem.paths) ? navItem.paths : [navItem.paths];

          // Check if current path matches any of the nav item's paths
          const isActive = pathArray.some((path) => {
            // Root path requires special handling
            if (path === '/') {
              return currentPath === '/' || currentPath.startsWith('/forms/');
            }
            return currentPath.startsWith(path);
          });

          if (isActive) {
            buttonElement.classList.add('active');
          }
        }
      });
    };

    updateActiveNavigation();

    const timeoutId = setTimeout(updateActiveNavigation, 300);
    return () => clearTimeout(timeoutId);
  }, [location.pathname, navigate, navigationItems]);
};
